/**
 * What a drag looks like to the editor, worked out from rects.
 *
 * Sanity's overlay answers "where will it land?" with a 6px bar between two blocks and nothing else. The
 * bar says nothing about how much room the block takes, which position it ends up in, or whether the
 * drop changes anything. These helpers turn the bar's rect plus the dragged block's own rect into that.
 */

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type Flow = 'vertical' | 'horizontal';

/** The overlay's insert bar is hard-coded to this colour (`OverlayDragInsertMarker`); it is the only handle on it. */
const MARKER_COLOR = 'rgb(85, 107, 252)';

/** A ghost taller or wider than this share of the viewport hides what it is being placed among. */
const MAX_VIEWPORT_SHARE = 0.6;

export const boxOf = (rect: DOMRect): Box => ({
  top: rect.top,
  left: rect.left,
  width: rect.width,
  height: rect.height,
});

export const unionOf = (boxes: readonly Box[]): Box | undefined => {
  if (boxes.length === 0) return undefined;
  const top = Math.min(...boxes.map(box => box.top));
  const left = Math.min(...boxes.map(box => box.left));
  const bottom = Math.max(...boxes.map(box => box.top + box.height));
  const right = Math.max(...boxes.map(box => box.left + box.width));
  return { top, left, width: right - left, height: bottom - top };
};

/**
 * The overlay's insert bar, if one is showing. It has no class or attribute, so it is recognised by
 * what it is: the blue pill the overlay draws above everything else, inside its own element.
 * If a Sanity update changes that, this finds nothing and the editor keeps Sanity's bar alone.
 */
export const findInsertMarker = (root: ParentNode = document): Element | undefined =>
  Array.from(root.querySelectorAll('sanity-visual-editing [style*="z-index: 999999"]')).find(
    element => getComputedStyle(element).backgroundColor === MARKER_COLOR,
  );

/** The bar is wide in a vertical list and tall in a row. */
export const flowOf = (marker: Box): Flow => (marker.height > marker.width ? 'horizontal' : 'vertical');

const centreOf = (box: Box, flow: Flow): number =>
  flow === 'vertical' ? box.top + box.height / 2 : box.left + box.width / 2;

/**
 * The space the block will take: its own size, anchored at the bar and drawn from it onwards — below in a
 * list, to the right in a row. The bar says where, never how big: its length follows the neighbours.
 * Capped, so a full-height hero does not blank the screen it is being placed on.
 */
export const ghostFor = (marker: Box, source: Box, flow: Flow, viewport: { width: number; height: number }): Box =>
  flow === 'vertical'
    ? {
        top: marker.top + marker.height / 2,
        left: marker.left,
        width: Math.min(source.width, viewport.width),
        height: Math.min(source.height, viewport.height * MAX_VIEWPORT_SHARE),
      }
    : {
        top: marker.top,
        left: marker.left + marker.width / 2,
        width: Math.min(source.width, viewport.width * MAX_VIEWPORT_SHARE),
        height: Math.min(source.height, viewport.height),
      };

export interface DropDescription {
  /** 1-based place the block takes among the group. */
  position: number;
  total: number;
  /** Dropping here puts the block back where it already is. */
  isUnchanged: boolean;
}

/**
 * Where the bar sits among the group's other members, compared with where the dragged block sits now.
 * Index comparison rather than distance: a gap between blocks can be any size, but the order is exact.
 */
export const describeDrop = (others: readonly Box[], source: Box, marker: Box, flow: Flow): DropDescription => {
  const line = centreOf(marker, flow);
  const before = (limit: number): number => others.filter(box => centreOf(box, flow) < limit).length;
  const landing = before(line);
  return {
    position: landing + 1,
    total: others.length + 1,
    isUnchanged: landing === before(centreOf(source, flow)),
  };
};

export const labelFor = ({ position, total, isUnchanged }: DropDescription): string =>
  isUnchanged ? 'Stays here' : `Move here · ${position} of ${total}`;
